import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';

// Получаем IP-адрес сервера из URL
const API_URL = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? window.location.protocol + '//' + window.location.hostname + ':9000'
  : '';

function FileUpload({ open, onClose, onUpload, userRole }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [formData, setFormData] = useState({
    file_name: '',
    stage: '',
    category: '',
    type: '',
    project_code_full: '',
    project_code_short: '',
    project_name: '',
    sheet_number: '',
    is_separate_doc: false,
    box_number: '',
    folder_number: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchStages();
      fetchSubcategories();
      fetchProjects();
    }
  }, [open]);

  useEffect(() => {
    if (formData.stage) {
      fetchCategories(formData.stage);
    }
  }, [formData.stage]);

  const fetchStages = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/stages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Помилка завантаження стадій');
      const data = await response.json();
      setStages(data);
    } catch (error) {
      console.error('Помилка:', error);
      setError('Не вдалося завантажити стадії');
    }
  };

  const fetchCategories = async (stageId) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/categories?stage_id=${stageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Помилка завантаження категорій');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Помилка:', error);
      setError('Не вдалося завантажити категорії');
    }
  };

  const fetchSubcategories = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/subcategories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Помилка завантаження підкатегорій');
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error('Помилка:', error);
      setError('Не вдалося завантажити підкатегорії');
    }
  };

  const fetchProjects = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/prj`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Помилка завантаження проектів');
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Помилка:', error);
      setError('Не вдалося завантажити проекти');
    }
  };

  const handleStageChange = (e) => {
    setSelectedStage(e.target.value);
    setSelectedCategory('');
    fetchCategories(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };

  const handleProjectChange = (e) => {
    setSelectedProject(e.target.value);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFormData(prev => ({
        ...prev,
        file_name: selectedFile.name
      }));
    }
  };

  const handleMetadataChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_separate_doc' ? checked : value
    }));
  };

  const handleUpload = async () => {
    if (!file || !selectedStage || !selectedType || !selectedProject) {
      setError('Заповніть всі обовʼязкові поля');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("file_name", formData.file_name);
      uploadData.append("stage", selectedStage);
      if (selectedCategory) uploadData.append("category", selectedCategory);
      uploadData.append("type", selectedType);
      uploadData.append("project_name", selectedProject);
      uploadData.append("project_code_full", formData.project_code_full || "");
      uploadData.append("project_code_short", formData.project_code_short || "");
      uploadData.append("sheet_number", formData.sheet_number || "");
      uploadData.append("is_separate_doc", formData.is_separate_doc ? "1" : "0");
      uploadData.append("box_number", formData.box_number || "");
      uploadData.append("folder_number", formData.folder_number || "");

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/documents/upload`, {
        method: "POST",
        headers: { 'Authorization': `Bearer ${token}` },
        body: uploadData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Помилка завантаження файлу");
      }

      const result = await response.json();
      onUpload(result);
      onClose();
      alert('Файл успішно завантажено');
    } catch (error) {
      setError(error.message);
      console.error('Помилка:', error);
      alert('Помилка завантаження файлу: ' + error.message);
    } finally {
      setLoading(false);
      setProgress(0);
      setFile(null);
      setFormData({
        file_name: '',
        stage: '',
        category: '',
        type: '',
        project_code_full: '',
        project_code_short: '',
        project_name: '',
        sheet_number: '',
        is_separate_doc: false,
        box_number: '',
        folder_number: ''
      });
      setSelectedStage('');
      setSelectedCategory('');
      setSelectedType('');
      setSelectedProject('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Додавання нового документа</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth required>
            <InputLabel>Проект</InputLabel>
            <Select value={selectedProject} onChange={handleProjectChange} label="Проект">
              {projects.map((prj) => (
                <MenuItem key={prj.id} value={prj.name}>{prj.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel>Стадія</InputLabel>
            <Select value={selectedStage} onChange={handleStageChange} label="Стадія">
              {stages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>{stage.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {categories.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Категорія</InputLabel>
              <Select value={selectedCategory} onChange={handleCategoryChange} label="Категорія">
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth required>
            <InputLabel>Тип документа</InputLabel>
            <Select value={selectedType} onChange={handleTypeChange} label="Тип документа">
              {subcategories.map((subcategory) => (
                <MenuItem key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" component="label">
              Вибрати файл
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {file && <Typography variant="body2">{file.name}</Typography>}
          </Box>
          <TextField
            label="Назва файлу"
            name="file_name"
            value={formData.file_name}
            onChange={handleMetadataChange}
            required
          />
          <TextField
            label="Код проекту"
            name="project_code_full"
            value={formData.project_code_full}
            onChange={handleMetadataChange}
          />
          <TextField
            label="Короткий код проекту"
            name="project_code_short"
            value={formData.project_code_short}
            onChange={handleMetadataChange}
          />
          <TextField
            label="Номер аркуша"
            name="sheet_number"
            value={formData.sheet_number}
            onChange={handleMetadataChange}
          />
          <FormControlLabel
            control={
              <Switch
                name="is_separate_doc"
                checked={formData.is_separate_doc}
                onChange={handleMetadataChange}
              />
            }
            label="Окремий документ"
          />
          <TextField
            label="Номер коробки"
            name="box_number"
            value={formData.box_number}
            onChange={handleMetadataChange}
          />
          <TextField
            label="Номер папки"
            name="folder_number"
            value={formData.folder_number}
            onChange={handleMetadataChange}
          />
          {loading && (
            <Box sx={{ width: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading || !file}
        >
          Завантажити
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FileUpload; 